import React, { useState, useCallback } from 'react';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import SearchBar from './components/SearchBar';
import LoadingState from './components/LoadingState';
import ResultsPanel from './components/ResultsPanel';
import FeaturesGrid from './components/FeaturesGrid';
import ScanHistory from './components/ScanHistory';
import Footer from './components/Footer';

const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3024';

function App() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [loadingPercent, setLoadingPercent] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  const analyzeUrl = useCallback(async (targetUrl) => {
    if (!targetUrl.trim()) return;

    const urlToScan = targetUrl.trim();
    if (!/^https?:\/\//i.test(urlToScan) && !/^[a-zA-Z0-9]/.test(urlToScan)) {
      setError('Por favor, insira uma URL válida.');
      return;
    }

    const finalUrl = /^https?:\/\//i.test(urlToScan) ? urlToScan : `https://${urlToScan}`;

    setLoading(true);
    setError('');
    setResults(null);

    const steps = [
      { text: 'Resolvendo DNS...', target: 8 },
      { text: 'Verificando certificado SSL/TLS...', target: 16 },
      { text: 'Identificando IP e geolocalização...', target: 28 },
      { text: 'Detectando Web Application Firewall...', target: 40 },
      { text: 'Analisando estrutura da URL...', target: 50 },
      { text: 'Verificando reputação do domínio...', target: 60 },
      { text: 'Analisando padrões de phishing...', target: 70 },
      { text: 'Consultando blacklists...', target: 80 },
      { text: 'Executando heurísticas de segurança...', target: 90 },
      { text: 'Compilando resultados...', target: 95 },
    ];

    setLoadingPercent(0);
    let stepIndex = 0;
    let currentPercent = 0;

    const stepInterval = setInterval(() => {
      if (stepIndex < steps.length) {
        setLoadingStep(steps[stepIndex].text);
        const target = steps[stepIndex].target;
        stepIndex++;
        // Smooth increment toward target
        const incrementInterval = setInterval(() => {
          currentPercent += 1;
          if (currentPercent >= target) {
            currentPercent = target;
            clearInterval(incrementInterval);
          }
          setLoadingPercent(currentPercent);
        }, 40);
      }
    }, 550);

    try {
      const response = await fetch(`${API_BASE}/api/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: finalUrl })
      });

      if (!response.ok) {
        throw new Error('Erro ao analisar a URL. Tente novamente.');
      }

      const data = await response.json();
      setResults(data);

      setHistory(prev => {
        const newEntry = {
          url: finalUrl,
          score: data.score,
          classification: data.classification,
          timestamp: new Date().toLocaleString('pt-BR')
        };
        return [newEntry, ...prev.filter(h => h.url !== finalUrl)].slice(0, 10);
      });
    } catch (err) {
      setError(err.message || 'Erro de conexão com o servidor de análise.');
    } finally {
      // Animate to 100% before hiding
      setLoadingPercent(100);
      setLoadingStep('Concluído!');
      await new Promise(r => setTimeout(r, 500));
      clearInterval(stepInterval);
      setLoading(false);
      setLoadingStep('');
      setLoadingPercent(0);
    }
  }, []);

  const handleScan = () => analyzeUrl(url);
  const handleQuickScan = (exampleUrl) => {
    setUrl(exampleUrl);
    analyzeUrl(exampleUrl);
  };
  const handleHistoryClick = (historyUrl) => {
    setUrl(historyUrl);
    analyzeUrl(historyUrl);
  };

  return (
    <>
      <div className="bg-grid" />
      <div className="app-container">
        <Header />
        <main className="main-content">
          <HeroSection />
          <SearchBar
            url={url}
            setUrl={setUrl}
            onScan={handleScan}
            loading={loading}
          />

          {error && <div className="error-message">⚠️ {error}</div>}

          {loading && <LoadingState step={loadingStep} percent={loadingPercent} />}

          {results && !loading && <ResultsPanel results={results} />}

          {!results && !loading && (
            <>
              <FeaturesGrid />
              {history.length > 0 && (
                <ScanHistory history={history} onItemClick={handleHistoryClick} />
              )}
            </>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}

export default App;
